import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";
import { useRef, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { ChipItemTableCell } from "@similarweb/ui-components";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";

import { hasClipboardDelimiter, parseClipboard } from "../../../utils/parseClipboard";

import {
    StyledAddButton,
    StyledAddButtonText,
    StyledAddContainer,
    StyledChipContainer,
    StyledFieldContainer,
    StyledInput,
} from "./styles";

type InteractiveChipsTextInputProps = {
    className?: string;
    placeholder?: string;
    chips: readonly string[];
    type?: "text" | "number";
    onAddChips: (values: string[]) => void;
    onRemoveChip: (value: string) => void;
};

const InteractiveChipsTextInput = ({
    className,
    type = "text",
    chips,
    placeholder,
    onAddChips,
    onRemoveChip,
}: InteractiveChipsTextInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    function isValueValid() {
        return value.trim().length > 0;
    }

    function handleKeyUp(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter" && isValueValid()) {
            onAddChips([value.trim()]);
            setValue("");
        }

        if (e.key === "Escape") {
            setValue("");
            inputRef?.current?.blur();
        }
    }

    const handleAddChip = () => {
        if (isValueValid()) {
            onAddChips([value.trim()]);
            setValue("");
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value: targetValue } = e.target;

        if (type === "number") {
            const numericValue = targetValue.replace(/[^0-9.]/g, "");
            setValue(numericValue);
        } else {
            setValue(targetValue);
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const raw = e.clipboardData.getData("text");

        if (!hasClipboardDelimiter(raw)) {
            return;
        }

        e.preventDefault();

        const parsed = parseClipboard(raw, type === "number");

        if (parsed.length === 0) {
            return;
        }

        onAddChips(parsed);
        setValue("");
    };

    return (
        <StyledFieldContainer className={className}>
            {chips.length > 0 ? (
                <StyledChipContainer>
                    {chips.map((chip) => (
                        <ChipItemTableCell
                            text={chip}
                            key={`${chip}-chip`}
                            onClick={() => onRemoveChip(chip)}
                        />
                    ))}
                </StyledChipContainer>
            ) : null}
            <StyledInput
                type="text"
                value={value}
                ref={inputRef}
                placeholder={placeholder}
                onKeyUp={handleKeyUp}
                onChange={handleChange}
                onPaste={handlePaste}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            />
            {isFocused && isValueValid() ? (
                <StyledAddContainer>
                    <StyledAddButton
                        onClick={handleAddChip}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <SWReactIcons
                            iconName="plus"
                            iconColor={colorsPalettes.blue[400]}
                            size="xs"
                        />
                        <I18n translatable={{ key: "si.common.button.add" }} />
                        <StyledAddButtonText>{value}</StyledAddButtonText>
                    </StyledAddButton>
                </StyledAddContainer>
            ) : null}
        </StyledFieldContainer>
    );
};

export default InteractiveChipsTextInput;
